import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../api/client';
import { BottomNav } from '../../components/app/DarkTheme';
import { ChevronLeft, ChevronDown, ChevronUp, CreditCard, Calendar, PauseCircle } from 'lucide-react';

const formatDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '-';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

const daysRemaining = (endDate) => {
  if (!endDate) return 0;
  const end = new Date(endDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return Math.max(0, Math.ceil((end - today) / (1000 * 60 * 60 * 24)));
};

const isHolding = (pass) => {
  if (pass.status === 'held') return true;
  const today = new Date().toISOString().split('T')[0];
  return pass.holding_start && pass.holding_end && pass.holding_start <= today && today <= pass.holding_end;
};

const MemberPasses = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExpired, setShowExpired] = useState(false);
  const [showHoldings, setShowHoldings] = useState(false);

  useEffect(() => {
    if (!token) { navigate('/app/login'); return; }
    fetchPasses();
  }, [token, navigate]);

  const fetchPasses = async () => {
    try {
      const res = await apiClient.get(`/app/my-passes?token=${token}`);
      setData(res.data);
    } catch (error) {
      console.error('Failed to fetch passes:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0F1115]">
        <div className="text-[#52525B]">로딩 중...</div>
      </div>
    );
  }

  const activePass = data?.active?.[0] || null;
  const expired = data?.expired || [];
  // 홀딩 내역은 현재 이용권 여부와 관계없이 모든 이용권(현재/만료 포함)의 홀딩 이력을 표시.
  // 백엔드에서 이미 holding_start 내림차순으로 정렬되어 도착하지만, 만일을 대비해 한 번 더 정렬.
  const allHoldings = [...(data?.holdings || [])].sort(
    (a, b) => String(b.holding_start || '').localeCompare(String(a.holding_start || ''))
  );

  return (
    <div className="min-h-screen bg-[#0F1115] pb-24 text-white">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 pt-12 pb-4">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A1D23] active:scale-95 transition-transform"
          data-testid="back-btn"
        >
          <ChevronLeft className="h-5 w-5 text-[#A1A1AA]" />
        </button>
        <h1 className="text-lg font-bold">이용권</h1>
      </div>

      <div className="px-4 space-y-3">
        {/* ═══ 1. 현재 이용권 ═══ */}
        {activePass ? (
          <div
            data-testid="active-pass-card"
            className="bg-gradient-to-br from-[#1A1D23] to-[#22252E] rounded-2xl p-5 relative overflow-hidden border border-[#22252E]"
          >
            <div className="absolute -top-16 -right-16 w-32 h-32 bg-[#FF7A00]/10 rounded-full blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-2 mb-3">
                <CreditCard className="h-4 w-4 text-[#FF7A00]" />
                <span className="text-[#FF7A00]/80 text-xs uppercase tracking-wider">현재 이용권</span>
              </div>
              <h2 className="text-xl font-bold">{activePass.name}</h2>
              <p className="text-[#A1A1AA] text-sm mt-2">
                {formatDate(activePass.start_date)} ~ {formatDate(activePass.end_date)}
              </p>

              <div className="mt-5 pt-4 border-t border-[#2A2E38] flex items-center justify-between">
                {isHolding(activePass) ? (
                  <div className="flex items-center gap-2">
                    <PauseCircle className="h-4 w-4 text-[#A78BFA]" />
                    <span className="text-[#A78BFA] text-sm font-medium">홀딩 중</span>
                  </div>
                ) : (
                  <span className="text-[#52525B] text-sm">
                    {activePass.pass_type === 'count' ? '남은 횟수' : '남은 기간'}
                  </span>
                )}
                <div className="flex items-baseline gap-1">
                  {activePass.pass_type === 'count' ? (
                    <>
                      <span className={`text-3xl font-bold ${isHolding(activePass) ? 'text-[#A78BFA]' : 'text-[#FF7A00]'}`}>{activePass.remaining_count ?? 0}</span>
                      <span className="text-[#52525B] text-sm">회</span>
                    </>
                  ) : (
                    <>
                      <span className={`text-3xl font-bold ${isHolding(activePass) ? 'text-[#A78BFA]' : 'text-[#FF7A00]'}`}>{daysRemaining(activePass.end_date)}</span>
                      <span className="text-[#52525B] text-sm">일</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div data-testid="no-active-pass" className="bg-[#1A1D23] rounded-2xl p-6 text-center">
            <CreditCard className="h-8 w-8 text-[#2A2E38] mx-auto mb-2" />
            <p className="text-[#71717A] text-sm">현재 이용 중인 이용권이 없습니다.</p>
          </div>
        )}

        {/* ═══ 2. 현재 이용권 상세 정보 ═══ */}
        {activePass && (
          <div data-testid="active-pass-detail" className="bg-[#1A1D23] rounded-2xl p-5">
            <h3 className="text-white text-sm font-semibold mb-4">상세 정보</h3>
            <div className="space-y-3 text-sm">
              <DetailRow label="이용권 종류" value={activePass.pass_type === 'count' ? '횟수제' : '기간제'} />
              <DetailRow label="시작일" value={formatDate(activePass.start_date)} />
              <DetailRow label="종료일" value={formatDate(activePass.end_date)} />
              {activePass.pass_type === 'count' && (
                <>
                  <DetailRow label="최초 횟수" value={`${activePass.total_count ?? '-'} 회`} />
                  <DetailRow label="남은 횟수" value={`${activePass.remaining_count ?? 0} 회`} />
                </>
              )}
              {activePass.pass_type !== 'count' && (
                <DetailRow label="남은 일수" value={`${daysRemaining(activePass.end_date)} 일`} />
              )}
              <DetailRow
                label="상태"
                value={isHolding(activePass) ? '홀딩 중' : '이용 중'}
                valueClass={isHolding(activePass) ? 'text-[#A78BFA]' : 'text-[#22C55E]'}
              />
              {isHolding(activePass) && activePass.holding_start && activePass.holding_end && (
                <div className="flex items-center justify-between" data-testid="holding-period-row">
                  <span className="text-[#71717A]">홀딩 기간</span>
                  <span className="text-[#A78BFA] font-medium">
                    {formatDate(activePass.holding_start)} ~ {formatDate(activePass.holding_end)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ═══ 3. 지난 이용권 (접기/펼치기) ═══ */}
        <div className="bg-[#1A1D23] rounded-2xl overflow-hidden">
          <button
            onClick={() => setShowExpired(v => !v)}
            className="w-full px-5 py-4 flex items-center justify-between active:bg-[#22252E]"
            data-testid="toggle-expired-btn"
          >
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[#52525B]" />
              <span className="text-sm font-medium">지난 이용권 보기</span>
              {expired.length > 0 && (
                <span className="text-[11px] text-[#52525B]">({expired.length})</span>
              )}
            </div>
            {showExpired ? (
              <ChevronUp className="h-4 w-4 text-[#52525B]" />
            ) : (
              <ChevronDown className="h-4 w-4 text-[#52525B]" />
            )}
          </button>

          {showExpired && (
            <div data-testid="expired-list" className="px-5 pb-4 border-t border-[#22252E]">
              {expired.length === 0 ? (
                <p className="text-[#52525B] text-xs text-center py-6">지난 이용권이 없습니다.</p>
              ) : (
                <div className="space-y-2 pt-3">
                  {expired.map((p) => (
                    <div key={p.id} className="bg-[#0F1115] rounded-xl p-3 border border-[#22252E]">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{p.name}</span>
                        <span className="text-[10px] text-[#71717A] bg-[#22252E] px-2 py-0.5 rounded">
                          만료
                        </span>
                      </div>
                      <p className="text-[11px] text-[#52525B] mt-1">
                        {formatDate(p.start_date)} ~ {formatDate(p.end_date)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ═══ 4. 홀딩 내역 (접기/펼치기) ═══ */}
        <div className="bg-[#1A1D23] rounded-2xl overflow-hidden">
          <button
            onClick={() => setShowHoldings(v => !v)}
            className="w-full px-5 py-4 flex items-center justify-between active:bg-[#22252E]"
            data-testid="toggle-holdings-btn"
          >
            <div className="flex items-center gap-2">
              <PauseCircle className="h-4 w-4 text-[#52525B]" />
              <span className="text-sm font-medium">홀딩 내역 보기</span>
              {allHoldings.length > 0 && (
                <span className="text-[11px] text-[#52525B]">({allHoldings.length})</span>
              )}
            </div>
            {showHoldings ? (
              <ChevronUp className="h-4 w-4 text-[#52525B]" />
            ) : (
              <ChevronDown className="h-4 w-4 text-[#52525B]" />
            )}
          </button>

          {showHoldings && (
            <div data-testid="holdings-list" className="px-5 pb-4 border-t border-[#22252E]">
              {allHoldings.length === 0 ? (
                <p className="text-[#52525B] text-xs text-center py-6">적용된 홀딩 내역이 없습니다.</p>
              ) : (
                <div className="space-y-2 pt-3">
                  {allHoldings.map((h, idx) => (
                    <div key={idx} className="bg-[#0F1115] rounded-xl p-3 border border-[#22252E]">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          {h.pass_name && (
                            <div className="text-[12px] text-[#A1A1AA] font-medium truncate">
                              {h.pass_name}
                            </div>
                          )}
                          <div className="text-[12px] text-[#71717A] mt-0.5 tabular-nums">
                            {formatDate(h.holding_start)} ~ {formatDate(h.holding_end)}
                          </div>
                        </div>
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded flex-shrink-0 ${
                            h.status === '진행중'
                              ? 'bg-[#F59E0B]/15 text-[#F59E0B]'
                              : 'bg-[#22252E] text-[#71717A]'
                          }`}
                        >
                          {h.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-[11px] text-[#A78BFA] font-medium">
                          총 {h.holding_days}일 홀딩
                        </span>
                        {h.reason && (
                          <span className="text-[11px] text-[#52525B] truncate">· {h.reason}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-[#3A3F4A] mt-3">
                * 홀딩으로 늘어난 일수만큼 종료일이 자동 연장됩니다.
              </p>
            </div>
          )}
        </div>
      </div>

      <BottomNav active="home" />
    </div>
  );
};

const DetailRow = ({ label, value, valueClass = 'text-white' }) => (
  <div className="flex items-center justify-between">
    <span className="text-[#71717A]">{label}</span>
    <span className={`font-medium ${valueClass}`}>{value}</span>
  </div>
);

export default MemberPasses;
